import React, { useState, useEffect, useMemo } from "react";
import ReactSelect from "react-select";
import ReactTooltip from "react-tooltip";
import { Dropdown } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import "../../../assets/css/qaband.css";

import QaQcRow from "./QaQcRow";
import Portal from "../../common/components/Portal";
import ConfirmationModal from "../../common/components/ConfirmationModal";

import actions from "../actions";
import SingleQaIssuePreview from "./SingleQaIssuePreview";
import history from "../../../config/history";
import { showAlert } from "../../../config/utils";
import { bulkResetBreadCrumpData, findPrevPathFromBreadCrumpData, popBreadCrumpOnPageClose } from "../../../config/utils";
import AddIssueModal from "../../common/components/AddIssueModal";
import ReviewGateSelectOption from "./ReviewGateSelectOption";
import {
    REVIEW_GATE_STATUS,
    buildCriteriaChecklistHtml,
    canMoveToQaPassed,
    enrichReviewGateOptions,
    getQaPassedDisabledReason,
    getReadyForQaCriteria,
    getReviewGateApiPayload,
    getReviewGateConfirmCopy,
    getReviewGateStatus,
    normalizeImageList,
    REVIEW_GATE_OPTION_TIP_IDS,
    REVIEW_GATE_STATUS_CLASS
} from "../utils/qaGateValidation";

const qaHeaderSelectMenuPortal = typeof document !== "undefined" && document.body ? document.body : null;

const qaHeaderSelectStyles = {
    menuPortal: base => ({ ...base, zIndex: 10050 })
};

const reviewGateSelectStyles = {
    menuPortal: base => ({ ...base, zIndex: 10050 }),
    menu: base => ({
        ...base,
        zIndex: 10050,
        fontSize: 13,
        borderRadius: 6,
        overflow: "hidden",
        boxShadow: "0 4px 14px rgba(15, 23, 42, 0.12)"
    }),
    menuList: base => ({
        ...base,
        paddingTop: 4,
        paddingBottom: 4
    }),
    option: (base, state) => ({
        ...base,
        fontSize: 13,
        lineHeight: 1.35,
        padding: "9px 12px",
        fontWeight: 500,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        backgroundColor: state.isSelected ? "#edf2f7" : state.isFocused ? "#f8fafc" : base.backgroundColor
    }),
    singleValue: base => ({
        ...base,
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.3
    }),
    control: base => ({
        ...base,
        fontSize: 13,
        minHeight: 32
    })
};

function QaQcBand({
    isQaQcReviewerUser,
    recommendationId,
    refreshinfoDetails,
    showData,
    basicDetails,
    imageResponse,
    getAllImageList
}) {
    const [filterBy, setFilterBy] = useState("");
    const [sortby, setSortby] = useState("created_at");
    const [sortOrder, setSortOrder] = useState("desc");
    const [showConfirmation, setShowConfirmation] = useState(null);
    const [clearAllConfirmation, setClearAllConfirmation] = useState(false);
    const [pendingReviewGateConfirm, setPendingReviewGateConfirm] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [editIssue, setEditIssue] = useState(null);
    const [isIssueAdding, setIsIssueAdding] = useState(false);
    const [showReopenConfirm, setShowReopenConfirm] = useState(false);
    const [sendAllConfirm, setSendAllConfirm] = useState(false);

    const {
        getAllRecommendationReviewsResponse = {},
        updateRecommendationReviewResponse = {},
        getRecommendationByIdResponse = {},
        updateQaQcPassedResponse = {}
    } = useSelector(state => state.recommendationsReducer) || {};
    const filterOptions = [
        { label: "Pending", value: "pending" },
        { label: "Approved", value: "approved" },
        { label: "Resolved", value: "resolved" },
        { label: "Default ", value: "" }
    ];
    const sortOptions = [
        { label: "Added date", value: "created_at" },
        { label: "Updated date", value: "updated_at" },
        { label: "Status", value: "status" }
    ];

    const activeSortFieldLabel = sortOptions.find(o => o.value === sortby)?.label || "Added date";

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(
            actions.getAllRecommendationReviews({
                recommendation_id: recommendationId,
                order: {
                    [`recommendation_reviews.${sortby}`]: sortOrder
                }
            })
        );
    }, [sortby, sortOrder]);

    useEffect(() => {
        if (selectedRow) {
            dispatch(actions.getRecommendationReviewById(selectedRow));
        }
    }, [selectedRow]);

    useEffect(() => {
        if (getRecommendationByIdResponse) {
            const { success, id, code, project } = getRecommendationByIdResponse;
            if (success) {
                handleBreadCrumbs({ id, project_id: project?.id, code, project: project?.name });
            }
        }
    }, [getRecommendationByIdResponse]);

    useEffect(() => {
        if (updateRecommendationReviewResponse?.message) {
            if (updateRecommendationReviewResponse?.success) {
                if (showConfirmation === selectedRow) {
                    setSelectedRow(null);
                }
                setShowConfirmation(null);
                setEditIssue(null);
            }
            showAlert(updateRecommendationReviewResponse.message);
            dispatch(
                actions.getAllRecommendationReviews({
                    recommendation_id: recommendationId,
                    order: {
                        [`recommendation_reviews.${sortby}`]: sortOrder
                    }
                })
            );
            if (selectedRow) {
                dispatch(actions.getRecommendationReviewById(selectedRow));
            }
            refreshinfoDetails();
        }
    }, [updateRecommendationReviewResponse]);

    useEffect(() => {
        if (updateQaQcPassedResponse?.success) {
            refreshinfoDetails();
            setPendingReviewGateConfirm(null);
        }
    }, [updateQaQcPassedResponse]);

    useEffect(() => {
        setSelectedRow(null);
    }, [recommendationId]);

    useEffect(() => {
        if (!getAllImageList || !recommendationId) return;
        const images = normalizeImageList(imageResponse);
        if (!images.length) {
            getAllImageList(recommendationId, {
                limit: 100,
                offset: 1,
                image_sort: basicDetails?.image_sort
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recommendationId]);

    const handleEditIssueTitle = title => {
        dispatch(actions.updateRecommendationReview(editIssue?.id, { recommendation_review: { title } }));
    };

    const handleAddIssueComment = (id, comment) => {
        dispatch(
            actions.addReviewComment({
                recommendation_review_comment: {
                    comment,
                    recommendation_review_id: id,
                    creator_id: localStorage.getItem("userId")
                }
            })
        );
    };

    const handleEditIssueStatus = ({ id, status }) => {
        if (isQaQcReviewerUser) {
            dispatch(
                actions.updateRecommendationReviewStatusReviewer(id, {
                    status_action: status
                })
            );
        } else {
            dispatch(
                actions.updateRecommendationReviewStatusUser(id, {
                    status_action: status
                })
            );
        }
    };

    const handleSendAllNotification = () => {
        dispatch(actions.sendQaQcAllNotification({ recommendation_id: recommendationId }));
    };

    const handleSendReviewNotification = id => {
        dispatch(actions.sendReviewNotification(id));
    };

    const handleRowClicked = id => {
        setSelectedRow(prev => (prev === id ? null : id));
    };

    const currentReviewGateStatus = getReviewGateStatus(getRecommendationByIdResponse);

    const qaIssues = useMemo(
        () => getAllRecommendationReviewsResponse?.recommendation_reviews || [],
        [getAllRecommendationReviewsResponse?.recommendation_reviews]
    );

    const readyForQaCriteria = useMemo(
        () => getReadyForQaCriteria({ images: imageResponse, basicDetails, issues: qaIssues }),
        [imageResponse, basicDetails, qaIssues]
    );

    const criteriaChecklistHtml = useMemo(
        () => buildCriteriaChecklistHtml(readyForQaCriteria),
        [readyForQaCriteria]
    );

    const qaPassedDisabledReason = useMemo(() => getQaPassedDisabledReason(qaIssues), [qaIssues]);

    const reviewGateOptions = useMemo(
        () =>
            enrichReviewGateOptions({
                isQaQcReviewerUser,
                readyForQaCriteria,
                issues: qaIssues,
                currentStatus: currentReviewGateStatus
            }),
        [isQaQcReviewerUser, readyForQaCriteria, qaIssues, currentReviewGateStatus]
    );

    const qaPassedOption = useMemo(
        () => reviewGateOptions.find(option => option.value === REVIEW_GATE_STATUS.QA_PASSED),
        [reviewGateOptions]
    );
    const qaPassedTipHtml = qaPassedOption?.disabledTipHtml || null;
    const qaPassedTipText = qaPassedOption?.disabledTip || null;

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [criteriaChecklistHtml, qaPassedTipHtml, qaPassedTipText]);

    const submitReviewGateStatus = status => {
        const payload = getReviewGateApiPayload(status);
        if (!payload) return;
        dispatch(actions.qaQcPassedUpdate(recommendationId, payload));
    };

    const handleReviewGateChange = selected => {
        if (!selected || selected.isDisabled) return;

        const nextStatus = selected.value;
        if (nextStatus === currentReviewGateStatus) return;

        if (nextStatus === REVIEW_GATE_STATUS.QA_PASSED) {
            const readyValid = readyForQaCriteria.every(c => c.met);
            if (!isQaQcReviewerUser || qaPassedDisabledReason || !readyValid) return;
            if (!canMoveToQaPassed(currentReviewGateStatus)) return;
        }

        if (nextStatus === REVIEW_GATE_STATUS.IN_PROGRESS) {
            setPendingReviewGateConfirm(nextStatus);
        } else {
            submitReviewGateStatus(nextStatus);
        }
    };

    const handleReviewGateMenuOpen = () => {
        setTimeout(() => ReactTooltip.rebuild(), 0);
    };

    const renderReviewGateControl = () => {
        if (!showData) return null;

        const showReadOnlyPassed =
            !isQaQcReviewerUser && currentReviewGateStatus === REVIEW_GATE_STATUS.QA_PASSED;

        return (
            <div className="qa-band-header__field qa-band-header__field--review-gate">
                <span className="qa-band-header__field-label qa-band-header__field-label--review-gate" id="qa-header-review-gate-lbl">
                    QA Status
                    <button
                        type="button"
                        className="qa-band-header__criteria-info"
                        aria-label="View requirements for Ready for QA/QC"
                        data-for={REVIEW_GATE_OPTION_TIP_IDS[REVIEW_GATE_STATUS.READY_FOR_QA]}
                        data-tip=""
                        data-delay-show="300"
                        data-effect="solid"
                        data-place="left"
                        data-background-color="#1e293b"
                        data-class="qa-gate-criteria-tooltip"
                    >
                        <i className="fas fa-info-circle" aria-hidden="true" />
                    </button>
                </span>
                <div className="qa-band-header__field-control" aria-labelledby="qa-header-review-gate-lbl">
                    <ReactTooltip
                        id={REVIEW_GATE_OPTION_TIP_IDS[REVIEW_GATE_STATUS.READY_FOR_QA]}
                        className="qa-gate-criteria-tooltip"
                        globalEventOff="click"
                        html={true}
                        place="left"
                        effect="solid"
                        backgroundColor="#1e293b"
                        getContent={() => criteriaChecklistHtml}
                    />
                    {(qaPassedTipHtml || qaPassedTipText) && (
                        <ReactTooltip
                            id={REVIEW_GATE_OPTION_TIP_IDS[REVIEW_GATE_STATUS.QA_PASSED]}
                            className={qaPassedTipHtml ? "qa-gate-criteria-tooltip" : "review-gate-option-tooltip"}
                            globalEventOff="click"
                            html={!!qaPassedTipHtml}
                            place="left"
                            effect="solid"
                            backgroundColor="#1e293b"
                            getContent={() => qaPassedTipHtml || qaPassedTipText}
                        />
                    )}
                    {showReadOnlyPassed ? (
                        <span className="qa-band-header__gate-badge">QA/QC Passed</span>
                    ) : (
                        <div className="categ-select-box wid-sel-bx-110 pr-1 sel-def-bx qa-band-header__select qa-band-header__select--review-gate">
                            <ReactSelect
                                className={`react-select-container ${REVIEW_GATE_STATUS_CLASS[currentReviewGateStatus] || ""}`}
                                classNamePrefix="react-select"
                                value={reviewGateOptions.find(option => option.value === currentReviewGateStatus)}
                                onChange={handleReviewGateChange}
                                options={reviewGateOptions}
                                isSearchable={false}
                                menuPortalTarget={qaHeaderSelectMenuPortal}
                                styles={reviewGateSelectStyles}
                                menuPlacement="bottom"
                                onMenuOpen={handleReviewGateMenuOpen}
                                isOptionDisabled={option => option.isDisabled}
                                components={{ Option: ReviewGateSelectOption }}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const toggleAddIssueModal = () => {
        const needsReopenConfirm =
            currentReviewGateStatus === REVIEW_GATE_STATUS.QA_PASSED ||
            currentReviewGateStatus === REVIEW_GATE_STATUS.READY_FOR_QA;
        if (needsReopenConfirm && !isIssueAdding) {
            setShowReopenConfirm(true);
        } else {
            setIsIssueAdding(!isIssueAdding);
        }
    };

    const handleBreadCrumbs = data => {
        const currentBC = JSON.parse(sessionStorage.getItem("bc-data") || "[]");
        if (currentBC?.length < 2) {
            const { id, project_id, code, project } = data || {};
            let bc = [
                { index: 0, key: "main", name: "FCA Projects", path: "/project" },
                { index: 1, key: "buildingName", name: project, path: `/project/projectinfo/${project_id}/basicdetails` },
                { index: 2, key: "info", name: "Recommendations", path: `/project/projectinfo/${project_id}/recommendations` },
                { index: 3, key: "Name", name: code, path: `/recommendations/recommendationsinfo/${id}/maindetails` },
                { index: 4, key: "info", name: "Qa/Qc", path: `/recommendations/recommendationsinfo/${id}/qaqc` }
            ];
            bulkResetBreadCrumpData(bc);
        }
    };

    const renderReviewGateConfirmationModal = () => {
        if (!pendingReviewGateConfirm) return null;

        const { heading, message } = getReviewGateConfirmCopy(pendingReviewGateConfirm, currentReviewGateStatus);

        return (
            <Portal
                body={
                    <ConfirmationModal
                        heading={heading}
                        message={message}
                        onNo={() => setPendingReviewGateConfirm(null)}
                        onYes={() => submitReviewGateStatus(pendingReviewGateConfirm)}
                        type="cancel"
                    />
                }
                onCancel={() => setPendingReviewGateConfirm(null)}
            />
        );
    };

    const renderSendAllConfirmationModal = () => {
        if (!sendAllConfirm) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        heading="Send reminders for all issues?"
                        message="This will send a reminder notification about every QA/QC issue on this recommendation to the relevant recipients."
                        onNo={() => setSendAllConfirm(false)}
                        onYes={() => {
                            handleSendAllNotification();
                            setSendAllConfirm(false);
                        }}
                        isUnAssign
                        type="unassign"
                    />
                }
                onCancel={() => setSendAllConfirm(false)}
            />
        );
    };
    const renderClearAllConfirmationModal = () => {
        if (!clearAllConfirmation) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        heading={"Do you want to clear all issues?"}
                        message={"This action cannot be reverted, are you sure that you need to clear all items?"}
                        onNo={() => setClearAllConfirmation(null)}
                        onYes={() => {}}
                        type="unassign"
                    />
                }
                onCancel={() => setClearAllConfirmation(null)}
            />
        );
    };

    const renderEditIssueModal = () => {
        if (!editIssue) return null;
        return <Portal body={<AddIssueModal title={editIssue?.title} onSubmit={handleEditIssueTitle} onCancel={() => setEditIssue(null)} />} />;
    };
    const getData = () =>
        !!filterBy
            ? getAllRecommendationReviewsResponse?.recommendation_reviews?.filter(({ status }) => status === filterBy)
            : getAllRecommendationReviewsResponse?.recommendation_reviews || [];
    const renderConfirmationModal = () => {
        if (!showConfirmation) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        heading={"Do you want to delete this issue ?"}
                        message={"This action cannot be reverted, are you sure that you need to delete this item?"}
                        onNo={() => setShowConfirmation(null)}
                        onYes={() => dispatch(actions.deleteRecommendationReview(showConfirmation))}
                    />
                }
                onCancel={() => setShowConfirmation(null)}
            />
        );
    };

    const menuPlacement = inx => (getData()?.length > 9 && inx > getData()?.length - 3 ? "top" : "bottom");
    if (!showData) return <div className="tab-active qa-band" />;
    return (
        <div className="tab-active qa-band">
            <div className="dtl-sec col-md-12">
                {/* <div className="top-bar-search">
                    <input type="text" className="form-control" placeholder="Issue title" />
                    <button className="btn btn-add">
                        <i className="fas fa-plus"></i>
                    </button>
                </div> */}
                <header className="qa-band-header mb-2" aria-label="QA/QC recommendation header">
                    <ReactTooltip
                        id="qa-band-rec-desc"
                        className="rc-tooltip-custom-class"
                        globalEventOff="click"
                        place="bottom"
                        effect="solid"
                        backgroundColor="#007bff"
                    />
                    <div className="qa-band-header__title">
                        <div className="qa-band-header__title-inner">
                            <span className="qa-band-header__title-prefix">Recommendation</span>
                            <span
                                className="qa-band-header__title-text"
                                {...(basicDetails?.description
                                    ? {
                                          "data-for": "qa-band-rec-desc",
                                          "data-tip": `Recommendation: ${basicDetails.description}`,
                                          "data-delay-show": "400",
                                          "data-effect": "solid",
                                          "data-place": "bottom",
                                          "data-background-color": "#007bff"
                                      }
                                    : {})}
                            >
                                {basicDetails?.description || "—"}
                            </span>
                        </div>
                    </div>

                    <div className="qa-band-header__controls">
                        <div className="qa-band-header__tools">
                            <ReactTooltip id="filter-select" className="rc-tooltip-custom-class" globalEventOff="click" />
                            <div className="qa-band-header__field">
                                <span className="qa-band-header__field-label" id="qa-header-filter-lbl">
                                    Filter by status
                                </span>
                                <div
                                    className="qa-band-header__field-control"
                                    aria-labelledby="qa-header-filter-lbl"
                                    data-delay-show="500"
                                    data-tip="Show issues by status"
                                    data-effect="solid"
                                    data-for="filter-select"
                                    data-place="bottom"
                                    data-background-color="#007bff"
                                >
                                    <div className="categ-select-box wid-sel-bx-110 pr-1 sel-def-bx qa-band-header__select">
                                        <ReactSelect
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            value={filterOptions.find(option => option.value === filterBy)}
                                            onChange={({ value }) => {
                                                setFilterBy(value);
                                            }}
                                            options={filterOptions}
                                            isSearchable={false}
                                            menuPortalTarget={qaHeaderSelectMenuPortal}
                                            styles={qaHeaderSelectStyles}
                                            menuPlacement="auto"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="qa-band-header__field qa-band-header__field--sort-jira">
                                <span className="qa-band-header__field-label" id="qa-header-sort-lbl">
                                    Sort
                                </span>
                                <Dropdown className="qa-band-header__sort-dropdown" drop="down">
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        id="qa-jira-sort-toggle"
                                        className="qa-band-header__surface-btn qa-band-header__sort-trigger"
                                        aria-labelledby="qa-header-sort-lbl"
                                    >
                                        <span className="qa-band-header__sort-trigger-field">{activeSortFieldLabel}</span>
                                        <i
                                            className={`fas fa-long-arrow-alt-${sortOrder === "asc" ? "up" : "down"} qa-band-header__sort-trigger-arrow`}
                                            aria-hidden="true"
                                        />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu
                                        className="qa-band-header__sort-menu"
                                        popperConfig={{
                                            strategy: "fixed",
                                            modifiers: [
                                                { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
                                                { name: "flip", enabled: true }
                                            ]
                                        }}
                                    >
                                        <div className="qa-band-header__sort-menu-section" role="group" aria-label="Sort by field">
                                            {sortOptions.map(opt => (
                                                <label
                                                    key={opt.value}
                                                    className={`qa-band-header__sort-menu-row ${sortby === opt.value ? "is-active" : ""}`}
                                                >
                                                    <input
                                                        type="radio"
                                                        className="qa-band-header__sort-menu-radio"
                                                        name="qa-sort-field"
                                                        value={opt.value}
                                                        checked={sortby === opt.value}
                                                        onChange={() => setSortby(opt.value)}
                                                    />
                                                    <span className="qa-band-header__sort-menu-radio-ui" aria-hidden="true" />
                                                    <span className="qa-band-header__sort-menu-label">{opt.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <Dropdown.Divider className="qa-band-header__sort-menu-divider" />
                                        <div className="qa-band-header__sort-menu-section" role="group" aria-label="Sort direction">
                                            <label className={`qa-band-header__sort-menu-row ${sortOrder === "asc" ? "is-active" : ""}`}>
                                                <input
                                                    type="radio"
                                                    className="qa-band-header__sort-menu-radio"
                                                    name="qa-sort-order"
                                                    value="asc"
                                                    checked={sortOrder === "asc"}
                                                    onChange={() => setSortOrder("asc")}
                                                />
                                                <span className="qa-band-header__sort-menu-radio-ui" aria-hidden="true" />
                                                <span className="qa-band-header__sort-menu-label">Ascending</span>
                                            </label>
                                            <label className={`qa-band-header__sort-menu-row ${sortOrder === "desc" ? "is-active" : ""}`}>
                                                <input
                                                    type="radio"
                                                    className="qa-band-header__sort-menu-radio"
                                                    name="qa-sort-order"
                                                    value="desc"
                                                    checked={sortOrder === "desc"}
                                                    onChange={() => setSortOrder("desc")}
                                                />
                                                <span className="qa-band-header__sort-menu-radio-ui" aria-hidden="true" />
                                                <span className="qa-band-header__sort-menu-label">Descending</span>
                                            </label>
                                        </div>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                            <ReactTooltip id="send-all" className="rc-tooltip-custom-class" />
                            {isQaQcReviewerUser && (
                                <div className="qa-band-header__field">
                                    <span className="qa-band-header__field-label" id="qa-header-sendall-lbl">
                                        Notifications
                                    </span>
                                    <button
                                        type="button"
                                        className="qa-band-header__surface-btn qa-band-header__btn--send-all"
                                        aria-labelledby="qa-header-sendall-lbl"
                                        data-delay-show="500"
                                        data-tip="Send reminder notification about all issues"
                                        data-effect="solid"
                                        data-for="send-all"
                                        data-place="bottom"
                                        data-background-color="#007bff"
                                        onClick={() => setSendAllConfirm(true)}
                                    >
                                        Send All
                                        <img src="/img/send-view.svg" className="img-send" alt="" />
                                    </button>
                                </div>
                            )}
                            {renderReviewGateControl()}
                        </div>

                        <div className="qa-band-header__actions">
                            {isQaQcReviewerUser && !basicDetails?.locked && (
                                <button type="button" className="qa-band-header__add" onClick={toggleAddIssueModal}>
                                    <i className="fas fa-plus mr-1" aria-hidden="true" />
                                    Add QA/QC Issue
                                </button>
                            )}
                            <button
                                type="button"
                                className="qa-band-header__close"
                                onClick={() => {
                                    popBreadCrumpOnPageClose();
                                    history.push(findPrevPathFromBreadCrumpData());
                                }}
                                aria-label="Close QA/QC and go back"
                            >
                                <i className="fas fa-times mr-1" aria-hidden="true" />
                                Close
                            </button>
                        </div>
                    </div>
                </header>

                <div className="qa-band-outer-sec qa-table">
                    <div className="table-areas">
                        <div className="table-responsive">
                            {!getData()?.length && getAllRecommendationReviewsResponse?.success && (
                                <div className="table-topper efc-topr no-data-efci">
                                    <div class="col-md-12 otr-topr">
                                        <h3>No data found</h3>
                                    </div>
                                </div>
                            )}
                            <table className="table">
                                {getData()?.length ? (
                                    <thead>
                                        <tr>
                                            <th className="icon-th"> </th>
                                            <th className="query-txt">Name </th>
                                            <th className="text-center">Reviewer </th>

                                            {!selectedRow && <th className="text-center">Resolved Date & Time</th>}
                                            <th className="text-center">Added Date & Time</th>
                                            <th className="text-center">Status</th>
                                            <th className="query-cmt">Comments</th>
                                            {!selectedRow && isQaQcReviewerUser && <th className="text-center">Actions</th>}
                                        </tr>
                                    </thead>
                                ) : null}

                                <tbody>
                                    {getData()?.map((rowData, inx) => {
                                        const userId = localStorage.getItem("userId");
                                        const userRole = localStorage.getItem("role");

                                        const isReviewer = rowData?.requestor?.id === userId;
                                        const hasReviewerPermission = isReviewer || userRole === "super_admin";

                                        return (
                                            <QaQcRow
                                                rowData={rowData}
                                                key={rowData?.id}
                                                selectedRow={selectedRow}
                                                setSelectedRow={setSelectedRow}
                                                hasReviewerPermission={hasReviewerPermission}
                                                // onSubmit={handleEditIssueTitle} //line edit no longer needed
                                                onEdit={setEditIssue}
                                                handleRowClick={handleRowClicked}
                                                menuPlacement={menuPlacement(inx)}
                                                onStatusChange={handleEditIssueStatus}
                                                isQaQcReviewerUser={isQaQcReviewerUser}
                                                setShowConfirmation={setShowConfirmation}
                                                onSendNotification={handleSendReviewNotification}
                                            />
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {selectedRow && (
                        <SingleQaIssuePreview
                            selectedRow={selectedRow}
                            // onSubmit={handleEditIssueTitle} //line edit no longer needed
                            onEdit={setEditIssue}
                            onStatusChange={handleEditIssueStatus}
                            isQaQcReviewerUser={isQaQcReviewerUser}
                            onCommentSubmit={handleAddIssueComment}
                            setShowConfirmation={setShowConfirmation}
                            onSendNotification={handleSendReviewNotification}
                            onClose={() => setSelectedRow(null)}
                        />
                    )}
                </div>
            </div>
            {renderClearAllConfirmationModal()}
            {renderConfirmationModal()}
            {renderReviewGateConfirmationModal()}
            {renderSendAllConfirmationModal()}
            {renderEditIssueModal()}
            {isIssueAdding && <Portal body={<AddIssueModal onCancel={toggleAddIssueModal} />} onCancel={toggleAddIssueModal} />}
            {showReopenConfirm && (
                <Portal
                    body={
                        <ConfirmationModal
                            heading={
                                currentReviewGateStatus === REVIEW_GATE_STATUS.QA_PASSED
                                    ? "This recommendation is marked as QA/QC Passed."
                                    : "This recommendation is marked as Ready for QA/QC."
                            }
                            message={
                                "Creating a new QA/QC issue will move this recommendation to Reopened. Are you sure you want to continue?"
                            }
                            type="cancel"
                            onNo={() => setShowReopenConfirm(false)}
                            onYes={() => {
                                setIsIssueAdding(true);
                                setShowReopenConfirm(false);
                            }}
                        />
                    }
                    onCancel={() => setShowReopenConfirm(false)}
                />
            )}
        </div>
    );
}

export default QaQcBand;
